// src/core/utilities/uuid.ts
function generateUUID() {
  if (typeof globalThis.crypto?.randomUUID === "function") {
    return globalThis.crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (character) => {
    const random = Math.floor(Math.random() * 16);
    const value = character === "x" ? random : random & 3 | 8;
    return value.toString(16);
  });
}
function isoDate(date) {
  return date.toISOString();
}
function parseDate(value) {
  return value instanceof Date ? value : new Date(value);
}

// src/core/models/chat-message.ts
function createAttachmentMetadata(init) {
  return {
    id: init.id ?? generateUUID(),
    type: init.type,
    name: init.name,
    mimeType: init.mimeType,
    byteCount: init.byteCount,
    storageURL: init.storageURL,
    storagePath: init.storagePath,
    previewBase64: init.previewBase64,
    base64: init.base64
  };
}
function createChatMessage(init) {
  return {
    id: init.id ?? generateUUID(),
    role: init.role,
    content: init.content,
    createdAt: init.createdAt ?? /* @__PURE__ */ new Date(),
    metadata: init.metadata
  };
}
var ChatMessages = {
  user(content) {
    return createChatMessage({ role: "user", content });
  },
  assistant(content) {
    return createChatMessage({ role: "assistant", content });
  },
  system(content) {
    return createChatMessage({ role: "system", content });
  },
  tool(content) {
    return createChatMessage({ role: "tool", content });
  }
};
function chatMessageToJSON(message) {
  return {
    id: message.id,
    role: message.role,
    content: message.content,
    createdAt: message.createdAt.toISOString(),
    ...message.metadata !== void 0 && { metadata: message.metadata }
  };
}
function chatMessageFromJSON(json) {
  return createChatMessage({
    id: json.id,
    role: json.role,
    content: json.content,
    createdAt: parseDate(json.createdAt),
    ...json.metadata !== void 0 && { metadata: json.metadata }
  });
}

// src/core/models/multi-bar-tool-activity.ts
function createMultiBarToolApproval(init) {
  return {
    id: init.id,
    title: init.title,
    summary: init.summary,
    status: init.status,
    approveLabel: init.approveLabel ?? "Approve",
    approveForThreadLabel: init.approveForThreadLabel ?? "Approve for this Thread",
    rejectLabel: init.rejectLabel ?? "Reject",
    canApprove: init.canApprove ?? true,
    canApproveForThread: init.canApproveForThread ?? false,
    canReject: init.canReject ?? true
  };
}
function createMultiBarChatMessage(init) {
  return {
    id: init.id,
    role: init.role,
    content: init.content,
    displayKind: init.displayKind ?? "normal",
    ...init.approval !== void 0 && { approval: init.approval },
    ...init.toolActivityEvent !== void 0 && {
      toolActivityEvent: init.toolActivityEvent
    },
    ...init.questionnaireSummary !== void 0 && {
      questionnaireSummary: init.questionnaireSummary
    },
    attachments: init.attachments ?? [],
    failed: init.failed ?? false
  };
}
var MultiBarToolActivityProjection = Object.freeze({
  timeline(messages) {
    const timeline = [];
    let activityMessages = [];
    const flushActivityMessages = () => {
      if (activityMessages.length === 0) {
        return;
      }
      const trace = traceFromMessages(activityMessages);
      timeline.push({ type: "toolTrace", id: trace.id, trace });
      activityMessages = [];
    };
    for (const message of messages) {
      if (message.toolActivityEvent !== void 0) {
        activityMessages.push(message);
      } else {
        flushActivityMessages();
        timeline.push({
          type: "message",
          id: `message:${message.id}`,
          message
        });
      }
    }
    flushActivityMessages();
    return timeline;
  },
  displayPayload(raw) {
    try {
      const parsed = JSON.parse(raw);
      return JSON.stringify(sortJSONValue(parsed), null, 2);
    } catch {
      return raw;
    }
  }
});
var MultiBarToolActivityLabeler = Object.freeze({
  label(toolNames, callID) {
    if (toolNames.includes("navigate_to_app_resource_for_user")) {
      return "Opening app view";
    }
    const verbs = ["Fetching", "Looking at", "Checking", "Reading", "Reviewing", "Pulling"];
    const verb = verbs[stableIndex(callID, verbs.length)] ?? "Checking";
    return `${verb} ${toolActivitySubject(toolNames)}`;
  }
});
function traceFromMessages(messages) {
  if (messages.length === 0) {
    throw new TypeError("A tool activity trace requires at least one message.");
  }
  const builders = [];
  messages.forEach((message, messageIndex) => {
    const event = message.toolActivityEvent;
    if (event?.type !== "calls") {
      return;
    }
    event.calls.forEach((call, callIndex) => {
      const callID = nonEmpty(call.id);
      builders.push({
        order: { messageIndex, itemIndex: callIndex },
        id: `tool-step:${message.id}:${callIndex}:${callID ?? "call"}`,
        ...callID !== void 0 && { callID },
        toolName: nonEmpty(call.toolName) ?? "app_tool",
        activityLabel: nonEmpty(call.activityLabel) ?? "Using app tool",
        requestPayload: MultiBarToolActivityProjection.displayPayload(call.arguments),
        callMessageID: message.id,
        isError: false
      });
    });
  });
  messages.forEach((message, messageIndex) => {
    const event = message.toolActivityEvent;
    if (event?.type !== "result") {
      return;
    }
    const callID = nonEmpty(event.callID);
    const matchingBuilder = builders.find(
      (builder) => builder.callID !== void 0 && builder.callID === callID && builder.resultMessageID === void 0
    );
    if (matchingBuilder !== void 0) {
      matchingBuilder.responsePayload = MultiBarToolActivityProjection.displayPayload(event.payload);
      matchingBuilder.resultMessageID = message.id;
      matchingBuilder.isError = event.isError;
      if (event.approval !== void 0) {
        matchingBuilder.approval = event.approval;
      }
      return;
    }
    builders.push({
      order: { messageIndex, itemIndex: 0 },
      id: `tool-step:${message.id}:result`,
      ...callID !== void 0 && { callID },
      toolName: nonEmpty(event.toolName) ?? "app_tool",
      activityLabel: nonEmpty(event.activityLabel) ?? "Using app tool",
      responsePayload: MultiBarToolActivityProjection.displayPayload(event.payload),
      resultMessageID: message.id,
      isError: event.isError,
      ...event.approval !== void 0 && { approval: event.approval }
    });
  });
  const steps = builders.sort(compareStepBuilders).map(stepFromBuilder);
  const firstMessage = messages[0];
  if (firstMessage === void 0) {
    throw new TypeError("A tool activity trace requires at least one message.");
  }
  const firstStep = steps[0];
  const title = firstStep === void 0 ? "Tool activity" : steps.length === 1 ? firstStep.activityLabel : `${firstStep.activityLabel} + ${steps.length - 1} more`;
  return {
    id: `tool-trace:${firstMessage.id}`,
    messageIDs: messages.map((message) => message.id),
    steps,
    title,
    hasPendingApproval: steps.some((step) => step.hasPendingApproval),
    hasError: steps.some((step) => step.isError)
  };
}
function stepFromBuilder(builder) {
  const status = statusFromBuilder(builder);
  return {
    id: builder.id,
    ...builder.callID !== void 0 && { callID: builder.callID },
    toolName: builder.toolName,
    activityLabel: builder.activityLabel,
    ...builder.requestPayload !== void 0 && {
      requestPayload: builder.requestPayload
    },
    ...builder.responsePayload !== void 0 && {
      responsePayload: builder.responsePayload
    },
    status,
    ...builder.approval !== void 0 && { approval: builder.approval },
    ...builder.callMessageID !== void 0 && {
      callMessageID: builder.callMessageID
    },
    ...builder.resultMessageID !== void 0 && {
      resultMessageID: builder.resultMessageID
    },
    isError: status === "failed",
    hasPendingApproval: status === "approvalPending"
  };
}
function statusFromBuilder(builder) {
  if (builder.isError) {
    return "failed";
  }
  if (builder.approval !== void 0) {
    const status = builder.approval.status.trim().toLowerCase();
    if (builder.approval.canApprove || builder.approval.canApproveForThread || builder.approval.canReject || status.includes("pending")) {
      return "approvalPending";
    }
    if (status.includes("reject") || status.includes("declin") || status.includes("expir")) {
      return "approvalRejected";
    }
    if (status.includes("approv") || status.includes("execut")) {
      return "approvalApproved";
    }
  }
  return builder.resultMessageID === void 0 ? "awaitingResponse" : "succeeded";
}
function compareStepBuilders(lhs, rhs) {
  if (lhs.order.messageIndex !== rhs.order.messageIndex) {
    return lhs.order.messageIndex - rhs.order.messageIndex;
  }
  return lhs.order.itemIndex - rhs.order.itemIndex;
}
function sortJSONValue(value) {
  if (Array.isArray(value)) {
    return value.map(sortJSONValue);
  }
  if (isRecord(value)) {
    return Object.fromEntries(
      Object.keys(value).sort().map((key) => [key, sortJSONValue(value[key])])
    );
  }
  return value;
}
function isRecord(value) {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
function nonEmpty(value) {
  const trimmed = value?.trim();
  return trimmed === void 0 || trimmed.length === 0 ? void 0 : trimmed;
}
function stableIndex(value, count) {
  if (count === 0) {
    return 0;
  }
  let sum = 0;
  for (const scalar of value) {
    sum += scalar.codePointAt(0) ?? 0;
  }
  return Math.abs(sum) % count;
}
function toolActivitySubject(toolNames) {
  const names = new Set(toolNames);
  if ([...names].some((name) => name.startsWith("get_connectwise"))) {
    if (names.has("get_connectwise_ticket_list_for_user")) {
      return "ticket board";
    }
    if (names.has("get_connectwise_ticket_detail_for_user")) {
      return "ticket details";
    }
    if (names.has("get_connectwise_project_list_for_user")) {
      return "ConnectWise projects";
    }
    if (names.has("get_connectwise_project_detail_for_user")) {
      return "project details";
    }
    if (names.has("get_connectwise_time_entries_for_user")) {
      return "time entries";
    }
    if (names.has("get_connectwise_expenses_for_user")) {
      return "expenses";
    }
    return "ConnectWise data";
  }
  if ([...names].some((name) => name.startsWith("prepare_connectwise")) || names.has("execute_approved_connectwise_write_action")) {
    if (names.has("prepare_connectwise_internal_note_for_user")) {
      return "note approval";
    }
    if (names.has("prepare_connectwise_ticket_time_entry_for_user") || names.has("prepare_connectwise_time_entry_adjustment_for_user") || names.has("prepare_connectwise_time_entry_deletion_for_user")) {
      return "time entry approval";
    }
    if (names.has("prepare_connectwise_ticket_expense_for_user") || names.has("prepare_connectwise_expense_adjustment_for_user")) {
      return "expense approval";
    }
    return "ConnectWise action";
  }
  if (names.has("get_schedule_view_for_user")) {
    return "event schedules";
  }
  if (names.has("get_event_detail_for_user")) {
    return "event details";
  }
  if (names.has("create_app_deep_link_citation_for_user")) {
    return "app citation";
  }
  if (names.has("navigate_to_app_resource_for_user")) {
    return "app view";
  }
  return "app data";
}

// src/core/models/chat-tool.ts
function chatToolParameters(properties, required = []) {
  return { type: "object", properties, required };
}
var emptyChatToolParameters = chatToolParameters({});
function createChatToolDefinition(init) {
  return {
    id: init.name,
    name: init.name,
    displayName: init.displayName,
    description: init.description,
    category: init.category,
    isEnabled: init.isEnabled ?? true,
    isReadOnly: init.isReadOnly ?? true,
    requiresApproval: init.requiresApproval ?? false,
    executionMode: init.executionMode ?? "immediate",
    parameters: init.parameters ?? emptyChatToolParameters
  };
}
function chatToolPropertyToJSON(property) {
  return {
    type: property.type,
    description: property.description,
    ...property.enumValues !== void 0 && { enum: property.enumValues },
    ...property.items !== void 0 && { items: property.items }
  };
}
function decodeChatToolArguments(json) {
  const parsed = JSON.parse(json);
  if (!isPlainObject(parsed)) {
    throw new TypeError("Tool arguments must decode to a JSON object.");
  }
  return parsed;
}
function chatToolStringValue(value) {
  return typeof value === "string" ? value : void 0;
}
function chatToolBoolValue(value) {
  return typeof value === "boolean" ? value : void 0;
}
function chatToolIntValue(value) {
  return typeof value === "number" && Number.isFinite(value) ? Math.trunc(value) : void 0;
}
function createChatToolExecutionResult(init) {
  return init;
}
function chatToolErrorResult(callId, toolName, reason) {
  return {
    callId,
    toolName,
    content: JSON.stringify({ error: reason, ok: "false", tool: toolName }, Object.keys({ error: reason, ok: "false", tool: toolName }).sort()),
    isError: true
  };
}
var ChatToolRegistry = class {
  toolsByName;
  constructor(tools = []) {
    this.toolsByName = new Map(tools.map((tool) => [tool.definition.name, tool]));
  }
  get toolDefinitions() {
    return [...this.toolsByName.values()].map((tool) => tool.definition).sort((lhs, rhs) => lhs.displayName.localeCompare(rhs.displayName));
  }
  async execute(request) {
    const outcome = await this.executionOutcome(request);
    if (outcome.type === "completed") {
      return outcome.result;
    }
    return chatToolErrorResult(
      request.callId,
      request.toolName,
      "This tool requires a foreground user-input capable chat runtime."
    );
  }
  async executionOutcome(request) {
    const tool = this.toolsByName.get(request.toolName);
    if (!tool) {
      return {
        type: "completed",
        result: chatToolErrorResult(request.callId, request.toolName, "Tool is not registered.")
      };
    }
    if (!tool.definition.isEnabled) {
      return {
        type: "completed",
        result: chatToolErrorResult(request.callId, request.toolName, "Tool is disabled.")
      };
    }
    if ("outcomeHandler" in tool) {
      return tool.outcomeHandler(request);
    }
    return {
      type: "completed",
      result: await tool.handler(request)
    };
  }
};
function isPlainObject(value) {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

// src/core/models/chat-tool-user-input.ts
var ChatToolUserInputValidationError = class extends Error {
  code;
  detail;
  constructor(code, detail) {
    super(validationErrorMessage(code, detail));
    this.name = "ChatToolUserInputValidationError";
    this.code = code;
    if (detail !== void 0) {
      this.detail = detail;
    }
  }
};
function createChatToolUserInputRequest(init) {
  const request = {
    id: `userInput:${init.callId}`,
    callId: init.callId,
    toolName: init.toolName,
    questionnaire: init.questionnaire
  };
  return validateChatToolUserInputRequest(request);
}
function chatToolUserInputRequestFromJSON(callId, toolName, questionnaireJSON) {
  return createChatToolUserInputRequest({
    callId,
    toolName,
    questionnaire: decodeChatToolUserInputQuestionnaire(questionnaireJSON)
  });
}
function decodeChatToolUserInputQuestionnaire(json) {
  let parsed;
  try {
    parsed = JSON.parse(json);
  } catch {
    throw validationError("invalidJSON");
  }
  try {
    return questionnaireFromJSONValue(parsed);
  } catch (error) {
    if (error instanceof ChatToolUserInputValidationError) {
      throw error;
    }
    throw validationError("invalidJSON");
  }
}
function encodeChatToolUserInputQuestionnaire(questionnaire) {
  return JSON.stringify(questionnaireToJSONValue(questionnaire));
}
function validateChatToolUserInputQuestionnaire(questionnaire) {
  if (!hasPlainObjectShape(questionnaire)) {
    throw validationError("invalidJSON");
  }
  if (questionnaire.schemaVersion !== 1) {
    throw validationError("unsupportedSchemaVersion", questionnaire.schemaVersion);
  }
  if (!Array.isArray(questionnaire.questions) || questionnaire.questions.length < 1 || questionnaire.questions.length > 3) {
    throw validationError("invalidQuestionCount");
  }
  validateOptionalText(questionnaire.title, "title", 120);
  validateOptionalText(questionnaire.context, "context", 500);
  const questionIds = /* @__PURE__ */ new Set();
  for (const question of questionnaire.questions) {
    if (!hasPlainObjectShape(question)) {
      throw validationError("invalidJSON");
    }
    validateIdentifier(question.id);
    if (questionIds.has(question.id)) {
      throw validationError("duplicateQuestionIdentifier", question.id);
    }
    questionIds.add(question.id);
    validateText(question.prompt, `question ${question.id} prompt`, 300);
    validateOptionalText(question.helpText, `question ${question.id} helpText`, 300);
    if (question.selectionMode !== "singleSelect" && question.selectionMode !== "multiSelect" || typeof question.isRequired !== "boolean" || typeof question.allowsOther !== "boolean") {
      throw validationError("invalidJSON");
    }
    if (!Array.isArray(question.options) || question.options.length < 2 || question.options.length > 5) {
      throw validationError("invalidOptionCount", question.id);
    }
    const optionIds = /* @__PURE__ */ new Set();
    for (const option of question.options) {
      if (!hasPlainObjectShape(option)) {
        throw validationError("invalidJSON");
      }
      validateIdentifier(option.id);
      if (optionIds.has(option.id)) {
        throw validationError("duplicateOptionIdentifier", option.id);
      }
      optionIds.add(option.id);
      validateText(option.label, `option ${option.id} label`, 100);
      validateOptionalText(
        option.description,
        `option ${option.id} description`,
        240
      );
    }
  }
  return { ...questionnaire, questions: questionnaire.questions.map((question) => ({ ...question, allowsOther: true })) };
}
function validateChatToolUserInputRequest(request) {
  if (!hasPlainObjectShape(request) || typeof request.callId !== "string" || request.callId.trim().length === 0 || typeof request.toolName !== "string" || request.toolName.trim().length === 0 || request.id !== `userInput:${request.callId}`) {
    throw validationError("requestMismatch");
  }
  return { ...request, questionnaire: validateChatToolUserInputQuestionnaire(request.questionnaire) };
}
function validateChatToolUserInputResponse(response, request) {
  validateChatToolUserInputRequest(request);
  if (!hasPlainObjectShape(response) || response.schemaVersion !== 1 || response.requestId !== request.id || !Array.isArray(response.answers)) {
    throw validationError("requestMismatch");
  }
  if (response.status === "cancelled") {
    if (response.answers.length !== 0) {
      throw validationError("requestMismatch");
    }
    return response;
  }
  if (response.status !== "submitted" || response.answers.length !== request.questionnaire.questions.length) {
    throw validationError("requestMismatch");
  }
  const answersByQuestionId = /* @__PURE__ */ new Map();
  for (const answer of response.answers) {
    if (!hasPlainObjectShape(answer)) {
      throw validationError("invalidAnswer", "unknown");
    }
    const questionId = typeof answer.questionId === "string" ? answer.questionId : "";
    const grouped = answersByQuestionId.get(questionId) ?? [];
    grouped.push(answer);
    answersByQuestionId.set(questionId, grouped);
  }
  for (const question of request.questionnaire.questions) {
    const grouped = answersByQuestionId.get(question.id);
    if (grouped?.length !== 1) {
      throw validationError("invalidAnswer", question.id);
    }
    const answer = grouped[0];
    if (answer === void 0 || !Array.isArray(answer.selectedOptionIds) || answer.otherText !== void 0 && typeof answer.otherText !== "string") {
      throw validationError("invalidAnswer", question.id);
    }
    const allowedIds = new Set(question.options.map((option) => option.id));
    const selectedIds = answer.selectedOptionIds;
    if (selectedIds.some((id) => typeof id !== "string") || new Set(selectedIds).size !== selectedIds.length || !selectedIds.every((id) => allowedIds.has(id))) {
      throw validationError("invalidAnswer", question.id);
    }
    const other = typeof answer.otherText === "string" ? answer.otherText.trim() : void 0;
    if (answer.skipped === true) {
      if (question.isRequired || selectedIds.length !== 0 || other !== void 0 && other.length !== 0) {
        throw validationError("invalidAnswer", question.id);
      }
      continue;
    }
    if (answer.skipped !== false) {
      throw validationError("invalidAnswer", question.id);
    }
    if (other !== void 0 && other.length !== 0 && graphemeCount(other) > 2e3) {
      throw validationError("invalidAnswer", question.id);
    }
    const hasOther = other !== void 0 && other.length !== 0;
    if (question.selectionMode === "singleSelect" && selectedIds.length + (hasOther ? 1 : 0) !== 1) {
      throw validationError("invalidAnswer", question.id);
    }
    if (selectedIds.length === 0 && !hasOther) {
      throw validationError("invalidAnswer", question.id);
    }
  }
  return response;
}
function createCancelledChatToolUserInputResponse(requestId) {
  return {
    schemaVersion: 1,
    requestId,
    status: "cancelled",
    answers: []
  };
}
function createChatToolUserInputDraft(requestId) {
  return {
    requestId,
    currentQuestionIndex: 0,
    answersByQuestionId: {},
    isCollapsed: false,
    isSubmitting: false
  };
}
function chatToolUserInputDraftAnswerIsValid(answer, question) {
  if (answer.isSkipped) {
    return !question.isRequired;
  }
  const other = answer.otherText.trim();
  const hasOther = answer.isOtherSelected && other.length !== 0;
  if (question.selectionMode === "singleSelect") {
    return answer.selectedOptionIds.length + (hasOther ? 1 : 0) === 1;
  }
  return answer.selectedOptionIds.length !== 0 || hasOther;
}
function createChatToolUserInputResponseFromDraft(request, draft) {
  validateChatToolUserInputRequest(request);
  if (draft.requestId !== request.id) {
    throw validationError("requestMismatch");
  }
  const answers = request.questionnaire.questions.map((question) => {
    const draftAnswer = draft.answersByQuestionId[question.id] ?? emptyDraftAnswer();
    const trimmedOther = draftAnswer.isOtherSelected ? draftAnswer.otherText.trim() : void 0;
    const answer = {
      questionId: question.id,
      selectedOptionIds: [...draftAnswer.selectedOptionIds],
      ...trimmedOther !== void 0 && trimmedOther.length !== 0 && { otherText: trimmedOther },
      skipped: draftAnswer.isSkipped
    };
    return answer;
  });
  const response = {
    schemaVersion: 1,
    requestId: request.id,
    status: "submitted",
    answers
  };
  return validateChatToolUserInputResponse(response, request);
}
function createChatToolUserInputResultEnvelope(request, response) {
  validateChatToolUserInputResponse(response, request);
  return {
    ok: true,
    tool: request.toolName,
    schemaVersion: response.schemaVersion,
    requestId: response.requestId,
    status: response.status,
    answers: response.answers
  };
}
function encodeChatToolUserInputResultEnvelope(request, response) {
  return stableJSONStringify(createChatToolUserInputResultEnvelope(request, response));
}
function questionnaireFromJSONValue(value) {
  if (!isPlainObject2(value)) {
    throw validationError("invalidJSON");
  }
  const schemaVersion = value.schemaVersion === void 0 ? 1 : requireNumber(value.schemaVersion);
  const title = optionalString(value.title);
  const context = optionalString(value.context);
  if (!Array.isArray(value.questions)) {
    throw validationError("invalidJSON");
  }
  const questionnaire = {
    schemaVersion,
    ...title !== void 0 && { title },
    ...context !== void 0 && { context },
    questions: value.questions.map(questionFromJSONValue)
  };
  return questionnaire;
}
function questionFromJSONValue(value) {
  if (!isPlainObject2(value) || !Array.isArray(value.options)) {
    throw validationError("invalidJSON");
  }
  const required = value.required === void 0 ? value.isRequired === void 0 ? false : requireBoolean(value.isRequired) : requireBoolean(value.required);
  if (value.allowsOther !== void 0) requireBoolean(value.allowsOther);
  const allowsOther = true;
  const helpText = optionalString(value.helpText);
  const selectionMode = requireString(value.selectionMode);
  if (selectionMode !== "singleSelect" && selectionMode !== "multiSelect") {
    throw validationError("invalidJSON");
  }
  return {
    id: requireString(value.id),
    prompt: requireString(value.prompt),
    ...helpText !== void 0 && { helpText },
    selectionMode,
    isRequired: required,
    allowsOther,
    options: value.options.map(optionFromJSONValue)
  };
}
function optionFromJSONValue(value) {
  if (!isPlainObject2(value)) {
    throw validationError("invalidJSON");
  }
  const description = optionalString(value.description);
  return {
    id: requireString(value.id),
    label: requireString(value.label),
    ...description !== void 0 && { description }
  };
}
function questionnaireToJSONValue(questionnaire) {
  return {
    schemaVersion: questionnaire.schemaVersion,
    ...questionnaire.title !== void 0 && { title: questionnaire.title },
    ...questionnaire.context !== void 0 && { context: questionnaire.context },
    questions: questionnaire.questions.map((question) => ({
      id: question.id,
      prompt: question.prompt,
      ...question.helpText !== void 0 && { helpText: question.helpText },
      selectionMode: question.selectionMode,
      required: question.isRequired,
      allowsOther: true,
      options: question.options.map((option) => ({
        id: option.id,
        label: option.label,
        ...option.description !== void 0 && {
          description: option.description
        }
      }))
    }))
  };
}
function emptyDraftAnswer() {
  return {
    selectedOptionIds: [],
    otherText: "",
    isOtherSelected: false,
    isSkipped: false
  };
}
function validateIdentifier(identifier) {
  if (typeof identifier !== "string" || !/^[A-Za-z0-9_-]{1,64}$/.test(identifier)) {
    throw validationError("invalidIdentifier", identifier);
  }
}
function validateText(text, field, maximum) {
  if (typeof text !== "string") {
    throw validationError("invalidText", field);
  }
  const trimmed = text.trim();
  if (trimmed.length === 0 || graphemeCount(trimmed) > maximum) {
    throw validationError("invalidText", field);
  }
}
function validateOptionalText(text, field, maximum) {
  if (text !== void 0) {
    validateText(text, field, maximum);
  }
}
function graphemeCount(text) {
  const segmenter = new Intl.Segmenter(void 0, { granularity: "grapheme" });
  return [...segmenter.segment(text)].length;
}
function stableJSONStringify(value) {
  return JSON.stringify(sortJSONValue2(value));
}
function sortJSONValue2(value) {
  if (Array.isArray(value)) {
    return value.map(sortJSONValue2);
  }
  if (!isPlainObject2(value)) {
    return value;
  }
  return Object.fromEntries(
    Object.keys(value).sort().map((key) => [key, sortJSONValue2(value[key])])
  );
}
function isPlainObject2(value) {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
function hasPlainObjectShape(value) {
  return isPlainObject2(value);
}
function optionalString(value) {
  if (value === void 0 || value === null) {
    return void 0;
  }
  return requireString(value);
}
function requireString(value) {
  if (typeof value !== "string") {
    throw validationError("invalidJSON");
  }
  return value;
}
function requireBoolean(value) {
  if (typeof value !== "boolean") {
    throw validationError("invalidJSON");
  }
  return value;
}
function requireNumber(value) {
  if (typeof value !== "number" || !Number.isInteger(value)) {
    throw validationError("invalidJSON");
  }
  return value;
}
function validationError(code, detail) {
  return new ChatToolUserInputValidationError(code, detail);
}
function validationErrorMessage(code, detail) {
  switch (code) {
    case "invalidJSON":
      return "questionnaireJson must be a valid JSON object matching schema version 1.";
    case "unsupportedSchemaVersion":
      return `Unsupported questionnaire schema version ${String(detail)}. Use schemaVersion 1.`;
    case "invalidQuestionCount":
      return "A questionnaire must contain between 1 and 3 questions.";
    case "invalidIdentifier":
      return `Question and option IDs must be 1-64 characters using letters, numbers, underscores, or hyphens. Invalid ID: ${String(detail)}`;
    case "duplicateQuestionIdentifier":
      return `Question IDs must be unique. Duplicate ID: ${String(detail)}`;
    case "duplicateOptionIdentifier":
      return `Option IDs must be unique within a question. Duplicate ID: ${String(detail)}`;
    case "invalidText":
      return `Questionnaire text is empty or exceeds the allowed length for ${String(detail)}.`;
    case "invalidOptionCount":
      return `Question ${String(detail)} must contain between 2 and 5 authored options.`;
    case "requestMismatch":
      return "The questionnaire response does not match the pending request.";
    case "invalidAnswer":
      return `The answer for question ${String(detail)} is missing or invalid.`;
  }
}
function chatToolUserInputSuggestedOptions(question) {
  return question.options.filter((option) => option.label.trim().toLowerCase() !== "other");
}

// src/core/services/chat-service.ts
var ChatServiceError = class _ChatServiceError extends Error {
  kind;
  providerName;
  causeError;
  constructor(kind, message, options = {}) {
    super(message);
    this.name = "ChatServiceError";
    this.kind = kind;
    this.providerName = options.providerName;
    this.causeError = options.cause;
  }
  static missingAPIKey(providerName) {
    return new _ChatServiceError("missingAPIKey", `API key not set for ${providerName}`, { providerName });
  }
  static networkError(error) {
    return new _ChatServiceError("networkError", `Network error: ${errorMessage(error)}`, { cause: error });
  }
  static invalidResponse() {
    return new _ChatServiceError("invalidResponse", "Invalid response from server");
  }
  static rateLimited() {
    return new _ChatServiceError("rateLimited", "Rate limited. Please try again later.");
  }
  static cancelled() {
    return new _ChatServiceError("cancelled", "Request was cancelled");
  }
  static unknown(message) {
    return new _ChatServiceError("unknown", message);
  }
};
var MockChatService = class {
  apiKeys = /* @__PURE__ */ new Map();
  abortController;
  async *sendMessage(message) {
    this.abortController = new AbortController();
    const signal = this.abortController.signal;
    const response = `This is a mock response to: ${message}`;
    for (const character of response) {
      if (signal.aborted) {
        throw ChatServiceError.cancelled();
      }
      await delay(20, signal);
      yield character;
    }
  }
  isConfigured(providerId) {
    return this.apiKeys.has(providerId);
  }
  setAPIKey(key, providerId) {
    this.apiKeys.set(providerId, key);
  }
  getAPIKey(providerId) {
    return this.apiKeys.get(providerId);
  }
  cancelCurrentRequest() {
    this.abortController?.abort();
    this.abortController = void 0;
  }
};
function errorMessage(error) {
  return error instanceof Error ? error.message : String(error);
}
function delay(milliseconds, signal) {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(ChatServiceError.cancelled());
      return;
    }
    const timeout = setTimeout(resolve, milliseconds);
    signal?.addEventListener(
      "abort",
      () => {
        clearTimeout(timeout);
        reject(ChatServiceError.cancelled());
      },
      { once: true }
    );
  });
}
async function* chatServiceEvents(service, message, history, model, systemPrompt) {
  if (service.sendMessageEvents) {
    yield* service.sendMessageEvents(message, history, model, systemPrompt);
  } else {
    for await (const text of service.sendMessage(message, history, model, systemPrompt)) yield { type: "textDelta", text };
    yield { type: "completion" };
  }
}

export { ChatMessages, ChatServiceError, ChatToolRegistry, ChatToolUserInputValidationError, MockChatService, MultiBarToolActivityLabeler, MultiBarToolActivityProjection, chatMessageFromJSON, chatMessageToJSON, chatServiceEvents, chatToolBoolValue, chatToolErrorResult, chatToolIntValue, chatToolParameters, chatToolPropertyToJSON, chatToolStringValue, chatToolUserInputDraftAnswerIsValid, chatToolUserInputRequestFromJSON, chatToolUserInputSuggestedOptions, createAttachmentMetadata, createCancelledChatToolUserInputResponse, createChatMessage, createChatToolDefinition, createChatToolExecutionResult, createChatToolUserInputDraft, createChatToolUserInputRequest, createChatToolUserInputResponseFromDraft, createChatToolUserInputResultEnvelope, createMultiBarChatMessage, createMultiBarToolApproval, decodeChatToolArguments, decodeChatToolUserInputQuestionnaire, emptyChatToolParameters, encodeChatToolUserInputQuestionnaire, encodeChatToolUserInputResultEnvelope, generateUUID, isoDate, parseDate, validateChatToolUserInputQuestionnaire, validateChatToolUserInputRequest, validateChatToolUserInputResponse };
//# sourceMappingURL=chunk-H465XLEU.js.map
//# sourceMappingURL=chunk-H465XLEU.js.map